import { get } from 'lodash-es';

import { emptyObject } from 'ui-components/default-props/empty';
import { FREIGHT_FFP_STATUS_CANCELLED } from '@shipstation/client-common/ltl/freight-ffp-status';
import { parseFreightStatus } from '@shipstation/client-common/components/freight-details/parse-freight-status';

export const selectFreightShipments = (state) => get(state, 'freightShipments', emptyObject);
export const selectFreightShipmentById = (state, props) => get(selectFreightShipments(state), props.freightShipmentId);

const isVoided = (shipment) => parseFreightStatus(shipment.status) === FREIGHT_FFP_STATUS_CANCELLED;

/**
 * Picks the *current* shipment between two candidates for the same plan: a live shipment always beats a
 * voided one, and among equals the higher `freightShipmentId` wins as the more recent.
 */
const preferCurrentShipment = (best, candidate) => {
  if (!best) {
    return candidate;
  }

  if (isVoided(best) !== isVoided(candidate)) {
    return isVoided(best) ? candidate : best;
  }

  return Number(candidate.freightShipmentId) > Number(best.freightShipmentId) ? candidate : best;
};

/**
 * The shipment a plan-keyed view should show for a plan: the most recent non-voided one, falling back to the
 * most recent voided one when every shipment for the plan has been voided.
 *
 * One plan accumulates several shipments across quote/book/void/re-book, and `entities.freightShipments` is
 * merged and never pruned — so once the LTL Voided grid has loaded voided shipments, a plan can have more
 * than one entry here. This previously returned whichever matched first, which for integer-like object keys
 * is the *lowest* id, i.e. the oldest shipment: after visiting the Voided view, plan-keyed views would show a
 * voided shipment's PRO number, lose the BOL link, and print the wrong BOL. The choice is now explicit
 * rather than an accident of key ordering.
 *
 * Voided-ness is read from the shipment's own `status`, normalized through `parseFreightStatus` so the
 * backend's variant spellings (`canceled`, mixed casing) count too. That helper is imported from its own
 * module rather than the `freight-details` barrel, which would pull Mantine and FontAwesome into the global
 * selector graph. The drawer's `isShipmentCancelled` additionally inspects `freightTrackingStatusId` to catch
 * a carrier-reported void the shipment row has not caught up with; that extra signal is deliberately not used
 * here, since a row this selector demotes is only ever chosen when the plan has nothing better.
 */
export const selectFreightShipmentByFulfillmentPlanId = (state, props) =>
  Object.values(selectFreightShipments(state)).reduce(
    (best, shipment) =>
      String(shipment.fulfillmentPlanId) === String(props.fulfillmentPlanId) ? preferCurrentShipment(best, shipment) : best,
    undefined,
  );
